import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl, defineMessages } from 'react-intl';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import Logger from 'js-logger';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles, Box, Typography } from '@material-ui/core';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as AddIcon } from '@lifesize/ux-assets/vectors/svgIcons/add-circle.svg';
import { ReactComponent as BlockIcon } from '@lifesize/ux-assets/vectors/svgIcons/block.svg';
import { ReactComponent as BlurIcon } from '@lifesize/ux-assets/vectors/svgIcons/blur.svg';
import { ReactComponent as CancelIcon } from '@lifesize/ux-assets/vectors/svgIcons/cancel-circle.svg';
import { Analytics } from '@lifesize/web-clients-core';
import { setError, baseBgOptions, bgOptType, bgSelectionType, defaultBgOptions } from 'reducers/vbbSettingsSlice';
import { TrackEvents } from 'utils/analytics';
import { onDragOver, onDrop, onImageSelected, errorType } from 'utils/images';
import { readFromLocalStorage, writeToLocalStorage } from 'utils/localStorage/vbbImages';
import { LsKey } from 'utils/localStorageUtils';
import { defaultVideoAspect } from 'utils/videoResolution';

const spacing = (multiplier) => multiplier * 8;
const thumbnailsPerRow = 4;

const messages = defineMessages({
  errCannotOptimizeImage: {
    id: 'components.vbbImagePicker.errCannotOptimizeImage',
    defaultMessage:
      'We experienced an issue modifying your image to be used as a virtual background. Please try again or use a different image.'
  },
  errCannotLoadImage: {
    id: 'components.vbbImagePicker.errCannotLoadImage',
    defaultMessage:
      'We experienced an issue uploading your image. It may be corrupted or too large; please try again or use a different image.'
  },
  errFileTypeNotSupported: {
    id: 'components.vbbImagePicker.errFileTypeNotSupported',
    defaultMessage:
      "Sorry, we don't support this file type! Please try again using one of our supported image types: {supportedTypes}."
  },
  chkboxSaveAsDefault: { id: 'components.vbbImagePicker.chkboxSaveAsDefault', defaultMessage: 'save as default' },
  titleVirtualBackground: {
    id: 'components.vbbImagePicker.titleVirtualBackground',
    defaultMessage: 'Virtual Background'
  }
});

const parseSelectionId = (selectionId: string) => {
  const isBaseBg = !!baseBgOptions.find((opt) => opt.id === selectionId);
  const isLifesizeBg = !!defaultBgOptions.find((opt) => opt.id === selectionId);
  return {
    type: isBaseBg ? selectionId : isLifesizeBg ? 'lifesize' : 'custom',
    id: isLifesizeBg ? selectionId : ''
  };
};

interface Props {
  intl: { formatMessage: Function };
  isJoinFlowCall?: boolean;
  isStartCallFlow?: boolean;
  onSelect: Function;
}

const ImagePicker = (props: Props) => {
  const classes = useStyles({});
  const dispatch = useDispatch();
  const inputRef = useRef<any>(null);
  const initialBgOptions: bgOptType[] = [
    {
      ...baseBgOptions.find((opt) => opt.id === 'none'),
      content: <BlockIcon className={classes.largeIcon} />,
      id: 'none'
    },
    {
      ...baseBgOptions.find((opt) => opt.id === 'blur'),
      content: <BlurIcon className={classes.largeIcon} />,
      id: 'blur'
    }
  ];
  const { intl } = props;
  const bgSelection: bgSelectionType = useSelector((state) => _get(state, 'app.vbbSettings.selection'));
  const [bgOptionsLocal, setBgOptionsLocal] = useState(readFromLocalStorage()[LsKey.VbbImages]);
  const [bgOptions, setBgOptions] = useState([...initialBgOptions, ...defaultBgOptions, ...bgOptionsLocal]);
  const [isDraggingOver, setIsDraggingOver] = useState<EventTarget | null>(null);

  const onImageLoaded = ({ dataUrl, error }: { dataUrl: string; error: any }) => {
    if (error) {
      Logger.warn('Error: onImageLoaded', error);
      Analytics.track(TrackEvents.VbbOptionLoaded, { successful: false, error: error.type });
      switch (error.type) {
        case errorType.CANNOT_OPTIMIZE:
          dispatch(setError(intl.formatMessage(messages.errCannotOptimizeImage)));
          break;
        case errorType.CANNOT_LOAD_IMAGE:
          dispatch(setError(intl.formatMessage(messages.errCannotLoadImage)));
          break;
        case errorType.FILE_TYPE_NOT_SUPPORTED:
          dispatch(
            setError(
              intl.formatMessage(messages.errFileTypeNotSupported, {
                supportedTypes: 'bmp, gif, jpeg, png, svg, tiff, webp'
              })
            )
          );
          break;
      }
    }
    if (dataUrl) {
      Analytics.track(TrackEvents.VbbOptionLoaded, { successful: true, error: '' });
      const newLocalOptions = [
        ...bgOptionsLocal,
        {
          dataUrl,
          id: uuidv4(),
          label: 'custom background'
        }
      ];
      onUpdateBgOptions(newLocalOptions);
    }
  };

  const onRemoveImage = (event: React.MouseEvent, optionId: string) => {
    const newLocalOptions = bgOptionsLocal.filter((option: bgOptType) => option.id !== optionId);

    event.stopPropagation();
    event.preventDefault();
    // select 'none' if you are deleting the currently selected bg
    if (bgSelection.id === optionId) {
      props.onSelect({ id: 'none' }, true);
    }
    onUpdateBgOptions(newLocalOptions);
  };

  // an image was selected with the form input (triggered by the '+' button)
  const onSelectImage = () => {
    const newFile = _get(inputRef, 'current.files[0]');

    if (newFile) {
      onImageSelected(newFile, onImageLoaded);
    }
  };

  const onUpdateBgOptions = (newLocalOptions: bgOptType[]) => {
    writeToLocalStorage({ [LsKey.VbbImages]: newLocalOptions });
    setBgOptionsLocal(newLocalOptions);
    setBgOptions([...initialBgOptions, ...defaultBgOptions, ...newLocalOptions]);
  };

  const renderDeleteButton = (optionId: string) => {
    return (
      <Box className={classes.bgOptionDelete} onClick={(event) => onRemoveImage(event, optionId)}>
        <CancelIcon />
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.headerTitle}>
          <Typography variant="h6">{intl.formatMessage(messages.titleVirtualBackground)}</Typography>
        </Box>
        <Box className={classes.headerControls}>
          <Box className={classes.headerBtn}>
            <AddIcon className={classes.headerBtnIcon} />
            <input
              accept="image/png, image/jpeg, image/gif, image/bmp, image/tiff, image/svg+xml, image/webp"
              onChange={onSelectImage}
              id="selectLocalImage"
              multiple={false}
              name="selectLocalImage"
              ref={inputRef}
              type="file"
            />
            <label htmlFor="selectLocalImage" /> {/* using label hides 'no file chosen' tooltip on file input */}
          </Box>
        </Box>
      </Box>
      <Box className={classes.scrollable}>
        <Box
          className={clsx(classes.thumbnails, isDraggingOver && classes.isDraggingOver)}
          onDragOver={onDragOver}
          onDrop={(event: React.DragEvent<HTMLElement>) => {
            onDrop(event, onImageLoaded);
            setIsDraggingOver(null);
          }} // onDrop will be fired instead of onDragLeave if the file is dropped on this target
          onDragEnter={(e) => setIsDraggingOver(e.target)} // onDrageEnter will be fired for this target element as well as any of its children
          onDragLeave={(e) => {
            e.target === isDraggingOver && setIsDraggingOver(null);
          }} // onDragLeave will fire when the draggable item leaves this target (or when the draggable item is dragged over one of its children, it occurs AFTER the onDrageEnter event of the child)
        >
          {bgOptions.map((option) => {
            const imgSrc = option.path || option.dataUrl;
            const optStyle = imgSrc ? { backgroundImage: `url('${imgSrc}')` } : {};
            return (
              <Box
                key={option.id}
                className={clsx(classes.bgOption, option.id === bgSelection.id && classes.bgSelected)}
                onClick={() => {
                  Analytics.track(TrackEvents.VbbOptionSelected, parseSelectionId(option.id));
                  props.onSelect(_pick(option, ['dataUrl', 'id', 'path']), true);
                }}
                style={optStyle}
              >
                <Box className={classes.bgOptionContent}>{option.content}</Box>
                {!!option.dataUrl && renderDeleteButton(option.id)}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

// Taken from AudioVideoSettings
const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    height: '100%'
  },
  header: {
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'row'
  },
  headerTitle: {
    alignItems: 'center',
    color: colors.headlineBlack,
    display: 'flex',
    flex: '1 0 170px',
    fontSize: '1.4rem'
  },
  headerControls: {
    flex: '0 0 auto'
  },
  headerBtn: {
    color: colors.lifesizeBlue,
    height: 32,
    position: 'relative',
    width: 32,
    '& input': {
      bottom: 0,
      height: 0,
      left: 0,
      opacity: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: 0
    },
    '& label': {
      bottom: 0,
      cursor: 'pointer',
      height: '100%',
      left: 0,
      opacity: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%'
    }
  },
  headerBtnIcon: {
    height: '100%',
    width: 'auto'
  },
  scrollable: {
    flex: '1 0 85px',
    marginTop: spacing(1.5),
    overflowX: 'hidden',
    overflowY: 'auto'
  },
  thumbnails: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 0,
    width: '100%'
  },
  isDraggingOver: {
    backgroundColor: colors.lifesizeBlue
  },
  largeIcon: {
    height: '80%',
    width: 'auto'
  },
  bgOption: {
    backgroundColor: '#ebf0f4',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    borderRadius: 4,
    boxSizing: 'border-box',
    color: colors.slate,
    cursor: 'pointer',
    height: 0,
    margin: spacing(0.5),
    paddingTop: `calc((100% / ${thumbnailsPerRow} - ${2 * spacing(0.5)}px) * (${defaultVideoAspect}))`, // magic to maintain aspect ratio
    position: 'relative',
    width: `calc(100% / ${thumbnailsPerRow} - ${2 * spacing(0.5)}px)`
  },
  bgOptionContent: {
    alignItems: 'center',
    bottom: 0,
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    padding: spacing(1),
    position: 'absolute',
    right: 0,
    top: 0,
    width: '100%'
  },
  bgOptionDelete: {
    height: spacing(4),
    position: 'absolute',
    right: 0,
    top: 0,
    width: spacing(4),
    '& svg': {
      color: colors.alertRed,
      height: '100%',
      width: 'auto'
    }
  },
  bgSelected: {
    boxShadow: `0 0 0 2px ${colors.lifesizeBlue}`
  }
}));

export default injectIntl(ImagePicker);
